import React from "react";

/**
 * Lightweight markdown-to-JSX renderer shared by article-style pages
 * (insights, comparisons, playbooks, templates, glossary). Supports:
 * headings (## ### ####), paragraphs, bullet/numbered lists, blockquotes,
 * inline bold (**), inline links [text](url), and custom block tokens of
 * the form `:::component-name:::` which are passed to `blockHandlers`.
 */
export type BlockHandler = (key: string) => React.ReactNode;

const renderInline = (text: string): React.ReactNode => {
  const linkParts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return linkParts.map((part, idx) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const isExternal = linkMatch[2].startsWith("http");
      return (
        <a
          key={idx}
          href={linkMatch[2]}
          className="text-teal-green underline underline-offset-2 hover:text-teal-green/80 transition-colors"
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {linkMatch[1]}
        </a>
      );
    }
    const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((bp, bi) => {
      if (bp.startsWith("**") && bp.endsWith("**")) {
        return <strong key={`${idx}-${bi}`}>{bp.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${idx}-${bi}`}>{bp}</React.Fragment>;
    });
  });
};

export function renderMarkdown(
  content: string,
  blockHandlers: Record<string, BlockHandler> = {},
): React.ReactNode[] {
  const blocks = content.split("\n\n");
  return blocks.map((block, i) => {
    const trimmed = block.trim();

    // Custom block token: :::name:::
    const tokenMatch = trimmed.match(/^:::([a-z0-9-]+):::$/);
    if (tokenMatch && blockHandlers[tokenMatch[1]]) {
      return (
        <React.Fragment key={i}>{blockHandlers[tokenMatch[1]](String(i))}</React.Fragment>
      );
    }

    if (trimmed.startsWith("#### ")) {
      return (
        <h4 key={i} className="text-lg md:text-xl font-bold text-deep-blue mt-8 mb-3 font-display">
          {trimmed.replace("#### ", "")}
        </h4>
      );
    }
    if (trimmed.startsWith("### ")) {
      return (
        <h3 key={i} className="text-xl md:text-2xl font-bold text-deep-blue mt-10 mb-3 font-display">
          {trimmed.replace("### ", "")}
        </h3>
      );
    }
    if (trimmed.startsWith("## ")) {
      const text = trimmed.replace("## ", "");
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      return (
        <h2
          key={i}
          id={id}
          className="text-2xl md:text-3xl font-bold text-deep-blue mt-12 mb-4 font-display scroll-mt-24"
        >
          {text}
        </h2>
      );
    }
    if (trimmed.startsWith("> ")) {
      const text = trimmed.split("\n").map((l) => l.replace(/^>\s?/, "")).join(" ").trim();
      return (
        <blockquote
          key={i}
          className="my-10 border-l-4 border-teal-green pl-6 pr-2 py-2 text-xl md:text-2xl font-display italic text-deep-blue leading-snug"
        >
          {renderInline(text)}
        </blockquote>
      );
    }
    if (/^-\s+/.test(trimmed)) {
      const items = trimmed.split(/\n(?=-\s+)/).map((l) => l.replace(/^-\s+/, "").trim());
      const allHaveBoldLead = items.every((it) => /^\*\*[^*]+:\*\*/.test(it));
      if (allHaveBoldLead) {
        return (
          <div key={i} className="mb-6 space-y-5">
            {items.map((it, j) => {
              const m = it.match(/^\*\*([^*]+):\*\*\s*(.*)$/s);
              return (
                <div key={j}>
                  <h4 className="text-lg md:text-xl font-bold text-deep-blue mb-2 font-display">
                    {m?.[1] ?? ""}
                  </h4>
                  <p className="text-deep-blue/80 leading-relaxed font-sans">
                    {renderInline(m?.[2] ?? "")}
                  </p>
                </div>
              );
            })}
          </div>
        );
      }
      return (
        <ul
          key={i}
          className="list-disc pl-6 mb-6 space-y-2 text-deep-blue/80 font-sans leading-relaxed marker:text-teal-green"
        >
          {items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = trimmed.split(/\n(?=\d+\.\s+)/).map((l) => l.replace(/^\d+\.\s+/, "").trim());
      return (
        <ol
          key={i}
          className="list-decimal pl-6 mb-6 space-y-2 text-deep-blue/80 font-sans leading-relaxed marker:text-teal-green marker:font-semibold"
        >
          {items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    }
    const subMatch = trimmed.match(/^\*\*([^*]+):\*\*\s*(.*)$/s);
    if (subMatch) {
      return (
        <div key={i} className="mb-6">
          <h4 className="text-lg md:text-xl font-bold text-deep-blue mt-6 mb-2 font-display">
            {subMatch[1]}
          </h4>
          <p className="text-deep-blue/80 leading-relaxed font-sans">{renderInline(subMatch[2])}</p>
        </div>
      );
    }
    return (
      <p key={i} className="text-deep-blue/80 leading-relaxed mb-4 font-sans">
        {renderInline(trimmed)}
      </p>
    );
  });
}

export function extractTOC(content: string): { id: string; text: string }[] {
  const matches: { id: string; text: string }[] = [];
  content.split("\n").forEach((line) => {
    const m = line.match(/^##\s+(.+)$/);
    if (m) {
      const text = m[1].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      matches.push({ id, text });
    }
  });
  return matches;
}
